import {
  addDays,
  differenceInCalendarDays,
  eachDayOfInterval,
  format,
  parseISO,
  startOfDay,
} from 'date-fns';
import type { Address, Coordinates } from '../types';
import { geocodeStructuredAddress } from './geocoding';

/** Inclusive forecast window: today through today + 9 (10 days). */
export const INSTALL_WEATHER_HORIZON_DAYS = 10;

export type InstallDayWeather = {
  ymd: string;
  weatherCode: number;
  precipitationSumMm: number;
  precipitationProbabilityMax: number | null;
  tempMaxF: number | null;
  tempMinF: number | null;
};

export type InstallWeatherSiteInput = {
  projectId: string;
  coordinates?: Coordinates;
  location?: string;
  address?: Address;
};

const CACHE_TTL_MS = 45 * 60 * 1000;

type ForecastCacheEntry = {
  fetchedAt: number;
  byYmd: Map<string, InstallDayWeather>;
};

const forecastCache = new Map<string, ForecastCacheEntry>();

function cacheKeyForCoords(coords: Coordinates): string {
  return `${coords.lat.toFixed(2)},${coords.lng.toFixed(2)}`;
}

export function ymdFromDate(d: Date): string {
  return format(startOfDay(d), 'yyyy-MM-dd');
}

export function parseYmd(ymd: string): Date {
  return startOfDay(parseISO(ymd));
}

/** True when date is today through today+9 (forecast horizon). Past dates are excluded. */
export function isDateWithinInstallWeatherHorizon(
  date: Date,
  today: Date = startOfDay(new Date()),
): boolean {
  const day = startOfDay(date);
  const offset = differenceInCalendarDays(day, today);
  return offset >= 0 && offset <= INSTALL_WEATHER_HORIZON_DAYS - 1;
}

export function filterYmdsWithinHorizon(ymds: string[], today?: Date): string[] {
  const t = today ?? startOfDay(new Date());
  const seen: Record<string, true> = {};
  const unique: string[] = [];
  for (let i = 0; i < ymds.length; i++) {
    const ymd = ymds[i];
    if (!seen[ymd]) {
      seen[ymd] = true;
      unique.push(ymd);
    }
  }
  return unique.filter(ymd => isDateWithinInstallWeatherHorizon(parseYmd(ymd), t));
}

/** WMO weather codes indicating drizzle, rain, showers, or thunderstorms. */
export function isRainyWmoWeatherCode(code: number): boolean {
  if (code >= 51 && code <= 67) return true;
  if (code >= 80 && code <= 82) return true;
  if (code >= 95 && code <= 99) return true;
  return false;
}

export function dayForecastHasPrecip(day: Pick<InstallDayWeather, 'precipitationSumMm' | 'weatherCode'>): boolean {
  if (day.precipitationSumMm > 0) return true;
  return isRainyWmoWeatherCode(day.weatherCode);
}

export function wmoWeatherCodeLabel(code: number): string {
  if (code === 0) return 'Clear';
  if (code <= 3) return 'Partly cloudy';
  if (code <= 48) return 'Fog';
  if (code <= 57) return 'Drizzle';
  if (code <= 67) return 'Rain';
  if (code <= 77) return 'Snow';
  if (code <= 82) return 'Rain showers';
  if (code <= 86) return 'Snow showers';
  if (code >= 95) return 'Thunderstorm';
  return 'Mixed';
}

function celsiusToF(c: number): number {
  return Math.round((c * 9) / 5 + 32);
}

export function formatInstallDayWeatherLine(day: InstallDayWeather, date?: Date): string {
  const d = date ?? parseYmd(day.ymd);
  const datePart = format(d, 'EEE MMM d');
  const label = wmoWeatherCodeLabel(day.weatherCode);
  const parts: string[] = [label];
  if (day.precipitationSumMm > 0) {
    parts.push(`${day.precipitationSumMm.toFixed(1)} mm`);
  }
  if (day.precipitationProbabilityMax != null && day.precipitationProbabilityMax > 0) {
    parts.push(`${Math.round(day.precipitationProbabilityMax)}%`);
  }
  if (day.tempMaxF != null && day.tempMinF != null) {
    parts.push(`${day.tempMaxF}°/${day.tempMinF}°`);
  }
  return `${datePart}: ${parts.join(' · ')}`;
}

export function listYmdsInTaskRange(start: Date, end: Date): string[] {
  const s = startOfDay(start);
  const e = startOfDay(end);
  if (e < s) return [];
  return eachDayOfInterval({ start: s, end: e }).map(ymdFromDate);
}

export function listHorizonYmdsInTaskRange(
  start: Date,
  end: Date,
  today: Date = startOfDay(new Date()),
): string[] {
  return listYmdsInTaskRange(start, end).filter(ymd =>
    isDateWithinInstallWeatherHorizon(parseYmd(ymd), today),
  );
}

type OpenMeteoDailyResponse = {
  daily?: {
    time?: string[];
    weather_code?: number[];
    precipitation_sum?: number[];
    precipitation_probability_max?: (number | null)[];
    temperature_2m_max?: number[];
    temperature_2m_min?: number[];
  };
};

/** @internal Exported for unit tests */
export function parseOpenMeteoDaily(data: OpenMeteoDailyResponse): Map<string, InstallDayWeather> {
  const out = new Map<string, InstallDayWeather>();
  const daily = data.daily;
  if (!daily?.time?.length) return out;

  const times = daily.time;
  for (let i = 0; i < times.length; i++) {
    const ymd = times[i];
    const weatherCode = daily.weather_code?.[i] ?? 0;
    const precipitationSumMm = daily.precipitation_sum?.[i] ?? 0;
    const precipProb = daily.precipitation_probability_max?.[i];
    const tMax = daily.temperature_2m_max?.[i];
    const tMin = daily.temperature_2m_min?.[i];
    out.set(ymd, {
      ymd,
      weatherCode,
      precipitationSumMm,
      precipitationProbabilityMax: precipProb ?? null,
      tempMaxF: tMax != null ? celsiusToF(tMax) : null,
      tempMinF: tMin != null ? celsiusToF(tMin) : null,
    });
  }
  return out;
}

export async function fetchOpenMeteoDailyForecast(coords: Coordinates): Promise<Map<string, InstallDayWeather>> {
  const key = cacheKeyForCoords(coords);
  const cached = forecastCache.get(key);
  if (cached && Date.now() - cached.fetchedAt < CACHE_TTL_MS) {
    return cached.byYmd;
  }

  const params = new URLSearchParams({
    latitude: String(coords.lat),
    longitude: String(coords.lng),
    daily: [
      'weather_code',
      'precipitation_sum',
      'precipitation_probability_max',
      'temperature_2m_max',
      'temperature_2m_min',
    ].join(','),
    timezone: 'auto',
    forecast_days: String(INSTALL_WEATHER_HORIZON_DAYS),
  });

  const res = await fetch(`https://api.open-meteo.com/v1/forecast?${params}`);
  if (!res.ok) {
    throw new Error(`Weather forecast failed (${res.status})`);
  }

  const json = (await res.json()) as OpenMeteoDailyResponse;
  const byYmd = parseOpenMeteoDaily(json);
  forecastCache.set(key, { fetchedAt: Date.now(), byYmd });
  return byYmd;
}

/** Clear in-memory forecast cache (tests). */
export function clearInstallWeatherForecastCache(): void {
  forecastCache.clear();
}

export async function resolveProjectSiteCoords(
  site: Pick<InstallWeatherSiteInput, 'coordinates' | 'location' | 'address'>,
): Promise<Coordinates | null> {
  if (site.coordinates?.lat != null && site.coordinates?.lng != null) {
    return site.coordinates;
  }
  if (site.address) {
    const fromAddr = await geocodeStructuredAddress(site.address);
    if (fromAddr) return fromAddr;
  }
  const loc = site.location?.trim();
  if (loc) {
    return geocodeStructuredAddress(loc);
  }
  return null;
}

export function coordsForHorizonEnd(today: Date = startOfDay(new Date())): Date {
  return addDays(today, INSTALL_WEATHER_HORIZON_DAYS - 1);
}
